import type { ReactNode } from "react";

import { Breadcrumb, type BreadcrumbItem } from "@/components/Breadcrumb";
import { SubNav } from "@/components/SubNav";
import type { SubNavTab } from "@/workloads/scope";

interface DetailShellProps {
  /** SubNav tab definitions for the current scope. */
  tabs: SubNavTab[];
  /** Currently active tab id. */
  activeTab: string;
  /** Called when the user clicks another tab. */
  onTabChange: (id: string) => void;
  /** Optional badge counts shown next to specific tabs. */
  badges?: Record<string, number | undefined>;
  /** Top-of-sidebar header (e.g. service name + scope eyebrow). */
  navTitle?: string;
  navSubtitle?: string;
  /**
   * If provided, the SubNav title becomes click-to-edit. Used only by
   * scopes whose title is a renameable display label (services); leave
   * undefined to keep the title read-only.
   */
  onRenameTitle?: (next: string) => void;
  /** Hover hint for the rename pencil (e.g. immutable slug). */
  renameHint?: string;
  /** Breadcrumb shown above the content area. */
  breadcrumb: BreadcrumbItem[];
  /** Body content for the active tab. */
  children: ReactNode;
}

/**
 * Layout wrapper used inside the bottom pane of the Workloads page. It places
 * the SubNav on the left and the breadcrumb + active-tab content on the right.
 *
 * Note: this shell used to expose a `headActions` slot for the small
 * "events" deep-link buttons that lived on the right of the
 * breadcrumb. Those entry points were unified into per-scope `Events`
 * SubNav tabs (and a `Logs` tab on Pool for SkyServe controller
 * logs) so observability surfaces all live in the left column —
 * removing the slot prevents the same drift from re-introducing
 * later.
 */
export function DetailShell({
  tabs,
  activeTab,
  onTabChange,
  badges,
  navTitle,
  navSubtitle,
  onRenameTitle,
  renameHint,
  breadcrumb,
  children,
}: DetailShellProps) {
  return (
    <div className="detail">
      <SubNav
        tabs={tabs}
        active={activeTab}
        onChange={onTabChange}
        badges={badges}
        title={navTitle}
        subtitle={navSubtitle}
        onRenameTitle={onRenameTitle}
        renameHint={renameHint}
      />
      <div className="detail__body">
        <header className="detail__head">
          <Breadcrumb items={breadcrumb} />
        </header>
        <div className="detail__content">{children}</div>
      </div>
    </div>
  );
}
